import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
/**
 * Custom hook that returns the effective clinic to use for orders
 * This will be the impersonated clinic if admin is impersonating,
 * otherwise the user's default clinic
 */
export const useEffectiveClinic = () => {
    const { user, isAuthenticated } = useAuth();
    const { isImpersonating, impersonatedClinic, selectedAddress } = useImpersonation();
    return useMemo(() => {
        // If impersonating, return the impersonated clinic data
        if (isImpersonating && impersonatedClinic) {
            const effectiveClinic = {
                clinic: {
                    // Spread all original clinic properties first
                    ...impersonatedClinic,
                    // Then override specific fields
                    address: {
                        // Use the selectedAddress object directly - it already has the correct format
                        ...selectedAddress,
                        // Also add underscore versions for compatibility
                        street_1: selectedAddress.street1 || selectedAddress.street_1,
                        street_2: selectedAddress.street2 || selectedAddress.street_2,
                        postal_code: selectedAddress.postalCode || selectedAddress.postal_code,
                    },
                },
                isAuthenticated: true, // Admin is authenticated
                isImpersonating: true,
            };
            return effectiveClinic;
        }
        // Otherwise return the user's default clinic
        const defaultResult = {
            clinic: user?.defaultClinic || null,
            isAuthenticated,
            isImpersonating: false,
        };
        return defaultResult;
    }, [
        isImpersonating,
        impersonatedClinic,
        selectedAddress,
        user?.defaultClinic,
        isAuthenticated,
    ]);
};
